import * as React from "react";
import {
  Box,
  IconButton,
  Popover,
  Tooltip,
  type TooltipProps,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

type ClickableProps = {
  onClick?: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
};

export type HelpTooltipProps = Omit<TooltipProps, "title" | "children"> & {
  title: React.ReactNode;
  children?: React.ReactElement<ClickableProps>;
  variant?: "info" | "question";
};

function isTouchLikeDevice(): boolean {
  if (typeof window === "undefined") return false;

  const hasTouchPoints = (navigator.maxTouchPoints ?? 0) > 0;
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  return hasTouchPoints || coarsePointer || "ontouchstart" in window;
}

export function HelpTooltip({
  title,
  children,
  variant = "info",
  ...tooltipProps
}: HelpTooltipProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const touch = React.useMemo(() => isTouchLikeDevice(), []);
  const open = Boolean(anchorEl);

  const handleOpen = (el: HTMLElement) => setAnchorEl(el);
  const handleClose = () => setAnchorEl(null);

  const defaultTrigger: React.ReactElement<ClickableProps> =
    variant === "question" ? (
      <Box
        role="button"
        tabIndex={0}
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "#d7e2eb",
          color: "#1A1A1A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          cursor: "pointer",
          fontWeight: 700,
          userSelect: "none",
        }}
      >
        ?
      </Box>
    ) : (
      <IconButton size="small">
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    );

  const trigger = children ?? defaultTrigger;

  // Desktop: Tooltip classico (hover/focus)
  if (!touch) {
    return (
      <Tooltip title={title} {...tooltipProps}>
        <Box component="span" sx={{ display: "inline-flex" }}>
          {trigger}
        </Box>
      </Tooltip>
    );
  }

  // Mobile: Popover al tap (click)
  const triggerWithHandlers = React.cloneElement(trigger, {
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(e);
      handleOpen(e.currentTarget);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      trigger.props.onKeyDown?.(e);
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpen(e.currentTarget);
      }
    },
  });

  return (
    <>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {triggerWithHandlers}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 1.5,
            maxWidth: 280,
            borderRadius: 2,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Box sx={{ fontSize: 14, color: "#1A1A1A", lineHeight: 1.4 }}>
          {title}
        </Box>
      </Popover>
    </>
  );
}
